import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  AddFileToReviewTypes,
  DeleteFileFromReviewTypes,
  ServiceReviewtypes,
  SuccesType,
  createReviewerTypes,
  editReviewerTypes,
  getReviewBySlugAndReviewerTypes,
  reviewTypes,
  reviewerTypes,
} from "./reviewTypes";
import reviewService from "./reviewService";
import { SuccessType } from "../authors/authorTypes";

interface reviewState {
  review: any | null;
  reviewer: reviewerTypes | null;
  reviewers: reviewerTypes[] | null;
  reviews: reviewTypes[] | null;
  loading: boolean;
  error: any | null;
  reviewersForArticle?: reviewerTypes[] | null;
}

const initialState: reviewState = {
  review: null,
  reviews: null,
  reviewer: null,
  reviewers: null,
  loading: false,
  error: null,
};

export const getReviewers = createAsyncThunk<
  reviewerTypes[],
  void,
  { rejectValue: string }
>("review/getReviewers", async (_, { rejectWithValue }) => {
  try {
    const response = await reviewService.getReviewers();
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const getReviewersForArticle = createAsyncThunk<
  reviewerTypes[],
  string,
  { rejectValue: string }
>("review/getReviewersForArticle", async (slug, { rejectWithValue }) => {
  try {
    const response = await reviewService.getReviewersForArticle(slug);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const createReviewer = createAsyncThunk<
  SuccesType,
  createReviewerTypes,
  { rejectValue: string }
>("reviewer/createReviewer", async (data, { rejectWithValue }) => {
  try {
    const response = await reviewService.createReviewer(data);
    return response;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || error.message || "Error desconocido";
    return rejectWithValue(errorMessage);
  }
});

export const editReviewer = createAsyncThunk<
  SuccessType,
  editReviewerTypes,
  { rejectValue: string }
>("reviewer/editReviewer", async (data, { rejectWithValue }) => {
  try {
    const response = await reviewService.editReviewer(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getMyPendingReviewsByStatus = createAsyncThunk<
  reviewTypes[],
  number[],
  { rejectValue: string }
>("review/pendingReviews", async (status: number[], { rejectWithValue }) => {
  try {
    const response = await reviewService.getMyPendingReviewsByStatus(status);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const assignReview = createAsyncThunk<
  SuccesType,
  ServiceReviewtypes,
  { rejectValue: string }
>(
  "review/assignReview",
  async (data: ServiceReviewtypes, { rejectWithValue }) => {
    try {
      const response = await reviewService.assignReview(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const acceptReview = createAsyncThunk<
  SuccesType,
  ServiceReviewtypes,
  { rejectValue: string }
>(
  "review/acceptReview",
  async (data: ServiceReviewtypes, { rejectWithValue }) => {
    try {
      const response = await reviewService.acceptReview(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const rejectReview = createAsyncThunk<
  SuccesType,
  ServiceReviewtypes,
  { rejectValue: string }
>(
  "review/rejectReview",
  async (data: ServiceReviewtypes, { rejectWithValue }) => {
    try {
      const response = await reviewService.rejectReview(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const sendReview = createAsyncThunk<
  SuccesType,
  ServiceReviewtypes,
  { rejectValue: string }
>(
  "review/sendReview",
  async (data: ServiceReviewtypes, { rejectWithValue }) => {
    try {
      const response = await reviewService.sendReview(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const addFileToReview = createAsyncThunk<
  SuccesType,
  AddFileToReviewTypes,
  { rejectValue: string }
>(
  "review/addFileToReview",
  async (data: AddFileToReviewTypes, { rejectWithValue }) => {
    try {
      const response = await reviewService.addFileToReview(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const deleteFileFromReview = createAsyncThunk<
  SuccesType,
  DeleteFileFromReviewTypes,
  { rejectValue: string }
>(
  "review/deleteFileFromReview",
  async (data: DeleteFileFromReviewTypes, { rejectWithValue }) => {
    try {
      const response = await reviewService.deleteFileFromReview(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getMyReviewsByStatus = createAsyncThunk<
  reviewTypes[],
  number,
  { rejectValue: string }
>(
  "review/getMyReviewsByStatus",
  async (status: number, { rejectWithValue }) => {
    try {
      const response = await reviewService.getMyReviewsByStatus(status);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const deleteReviewer = createAsyncThunk<
  SuccesType,
  string,
  { rejectValue: string }
>("author/deleteReviewer", async (name: string, { rejectWithValue }) => {
  try {
    const response = await reviewService.deleteReviewer(name);
    return response;
  } catch (error: any) {
    const backendErrorMessage =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.message ||
      "Error desconocido en el servidor";
    return rejectWithValue(backendErrorMessage);
  }
});

export const getReviewBySlug = createAsyncThunk<
  reviewTypes,
  string,
  { rejectValue: string }
>("articles/getReviewBySlug", async (slug: string, { rejectWithValue }) => {
  try {
    const article = await reviewService.getReviewBySlug(slug);
    return article;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getReviewBySlugAndReviewer = createAsyncThunk<
  reviewTypes,
  getReviewBySlugAndReviewerTypes,
  { rejectValue: string }
>(
  "articles/getReviewBySlugAndReviewer",
  async (data: getReviewBySlugAndReviewerTypes, { rejectWithValue }) => {
    try {
      const article = await reviewService.getReviewBySlugAndReviewer(data);
      return article;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getReviewBySlugByAuthor = createAsyncThunk<
  reviewTypes,
  string,
  { rejectValue: string }
>(
  "articles/getReviewBySlugByAuthor",
  async (slug: string, { rejectWithValue }) => {
    try {
      const article = await reviewService.getReviewBySlugByAuthor(slug);
      return article;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);
export const getReviewersBySlug = createAsyncThunk<
  reviewerTypes[],
  string,
  { rejectValue: string }
>("review/getReviewersBySlug", async (slug: string, { rejectWithValue }) => {
  try {
    const response = await reviewService.getReviewersBySlug(slug);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const getPendingReviews = createAsyncThunk<
  reviewTypes[],
  void,
  { rejectValue: string }
>("review/getPendingReviews", async (_, { rejectWithValue }) => {
  try {
    const response = await reviewService.getPendingReviews();
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const getMyCompletedReviews = createAsyncThunk<
  reviewTypes[],
  void,
  { rejectValue: string }
>("review/getMyCompletedReviews", async (_, { rejectWithValue }) => {
  try {
    const response = await reviewService.getMyCompletedReviews();
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const getMyClosedReviews = createAsyncThunk<
  reviewTypes[],
  void,
  { rejectValue: string }
>("review/getMyClosedReviews", async (_, { rejectWithValue }) => {
  try {
    const response = await reviewService.getMyclosedReviews();
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const getCertificateInfo = createAsyncThunk<
  reviewTypes[],
  number,
  { rejectValue: string }
>(
  "review/getCertificateInfo",
  async (idReview: number, { rejectWithValue }) => {
    try {
      const response = await reviewService.getCertificateInfo(idReview);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const generateCertificate = createAsyncThunk<
  SuccessType,
  number,
  { rejectValue: string }
>(
  "reviewer/generateCertificate",
  async (idReview: number, { rejectWithValue }) => {
    try {
      const response = await reviewService.generateCertificate(idReview);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getByCertificateCode = createAsyncThunk<
  reviewTypes[],
  string,
  { rejectValue: string }
>(
  "review/getByCertificateCode",
  async (certificateCode: string, { rejectWithValue }) => {
    try {
      const response =
        await reviewService.getByCertificateCode(certificateCode);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

const reviewSlice = createSlice({
  name: "review",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // 1. getReviewers
    builder.addCase(getReviewers.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.reviewers = null;
    });
    builder.addCase(getReviewers.fulfilled, (state, action) => {
      state.loading = false;
      state.reviewers = action.payload;
    });
    builder.addCase(getReviewers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
      state.reviewers = null;
    });

    // 1. getReviewersForArticle
    builder.addCase(getReviewersForArticle.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.reviewers = null;
    });
    builder.addCase(getReviewersForArticle.fulfilled, (state, action) => {
      state.loading = false;
      state.reviewers = action.payload;
    });
    builder.addCase(getReviewersForArticle.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
      state.reviewers = null;
    });

    // 2. createReviewer
    builder.addCase(createReviewer.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createReviewer.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createReviewer.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error al crear";
    });

    // 3. editReviewer
    builder.addCase(editReviewer.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(editReviewer.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(editReviewer.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error al editar";
    });

    // 4. getMyPendingReviewsByStatus
    builder.addCase(getMyPendingReviewsByStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.reviews = [];
    });
    builder.addCase(getMyPendingReviewsByStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.reviews = action.payload || [];
    });
    builder.addCase(getMyPendingReviewsByStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error obteniendo reviews pendientes";
      state.reviews = [];
    });

    // 5. assignReview
    builder.addCase(assignReview.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(assignReview.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(assignReview.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error al asignar";
    });

    // 6. acceptReview
    builder.addCase(acceptReview.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(acceptReview.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(acceptReview.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error al aceptar";
    });

    // 7. rejectReview
    builder.addCase(rejectReview.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(rejectReview.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rejectReview.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error al rechazar";
    });

    // 8. sendReview
    builder.addCase(sendReview.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(sendReview.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(sendReview.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error al enviar";
    });

    // 9. addFileToReview
    builder.addCase(addFileToReview.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addFileToReview.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(addFileToReview.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error al añadir archivo";
    });

    // 10. deleteFileFromReview
    builder.addCase(deleteFileFromReview.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteFileFromReview.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteFileFromReview.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error al eliminar archivo";
    });

    // 11. getMyReviewsByStatus
    builder.addCase(getMyReviewsByStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getMyReviewsByStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.reviews = action.payload || [];
    });
    builder.addCase(getMyReviewsByStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error obteniendo reviews";
    });

    // 12. deleteReviewer
    builder.addCase(deleteReviewer.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteReviewer.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteReviewer.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error al eliminar reviewer";
    });

    // 13. getReviewBySlug
    builder.addCase(getReviewBySlug.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.review = null;
    });
    builder.addCase(getReviewBySlug.fulfilled, (state, action) => {
      state.loading = false;
      state.review = action.payload;
    });
    builder.addCase(getReviewBySlug.rejected, (state, action) => {
      state.loading = false;
      state.review = null;
      state.error = action.payload || "Error obteniendo review";
    });

    // 14. getReviewBySlugAndReviewer
    builder.addCase(getReviewBySlugAndReviewer.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.review = null;
    });
    builder.addCase(getReviewBySlugAndReviewer.fulfilled, (state, action) => {
      state.loading = false;
      state.review = action.payload;
    });
    builder.addCase(getReviewBySlugAndReviewer.rejected, (state, action) => {
      state.loading = false;
      state.review = null;
      state.error = action.payload || "Error obteniendo review";
    });

    // 15. getReviewBySlugByAuthor
    builder.addCase(getReviewBySlugByAuthor.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.review = null;
    });
    builder.addCase(getReviewBySlugByAuthor.fulfilled, (state, action) => {
      state.loading = false;
      state.review = action.payload;
    });
    builder.addCase(getReviewBySlugByAuthor.rejected, (state, action) => {
      state.loading = false;
      state.review = null;
      state.error = action.payload || "Error obteniendo review";
    });

    // 16. getReviewersBySlug
    builder.addCase(getReviewersBySlug.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.reviewersForArticle = [];
    });
    builder.addCase(getReviewersBySlug.fulfilled, (state, action) => {
      state.loading = false;
      state.reviewersForArticle = action.payload || [];
    });
    builder.addCase(getReviewersBySlug.rejected, (state, action) => {
      state.loading = false;
      state.reviewersForArticle = [];
      state.error = action.payload || "Error obteniendo reviewers";
    });

    // 17. getPendingReviews
    builder.addCase(getPendingReviews.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.reviews = null;
    });
    builder.addCase(getPendingReviews.fulfilled, (state, action) => {
      state.loading = false;
      state.reviews = action.payload;
    });
    builder.addCase(getPendingReviews.rejected, (state, action) => {
      state.loading = false;
      state.reviews = null;
      state.error = action.payload || "Error obteniendo reviews";
    });

    // 18. getMyClosedReviews
    builder.addCase(getMyClosedReviews.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.reviews = [];
    });
    builder.addCase(getMyClosedReviews.fulfilled, (state, action) => {
      state.loading = false;
      state.reviews = action.payload || [];
    });
    builder.addCase(getMyClosedReviews.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error obteniendo reviews cerrados";
      state.reviews = [];
    });
    // 19 getMyCCompletedReviews
    builder.addCase(getMyCompletedReviews.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.reviews = [];
    });
    builder.addCase(getMyCompletedReviews.fulfilled, (state, action) => {
      state.loading = false;
      state.reviews = action.payload || [];
    });
    builder.addCase(getMyCompletedReviews.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error obteniendo reviews completados";
      state.reviews = [];
    });
    // 20 getCertificateInfo
    builder.addCase(getCertificateInfo.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.review = null;
    });
    builder.addCase(getCertificateInfo.fulfilled, (state, action) => {
      state.loading = false;
      state.review = action.payload || null;
    });
    builder.addCase(getCertificateInfo.rejected, (state, action) => {
      state.loading = false;
      state.error =
        action.payload || "Error obteniendo información de certificado";
      state.review = null;
    });
    // 21 generateCertificate
    builder.addCase(generateCertificate.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.review = null;
    });
    builder.addCase(generateCertificate.fulfilled, (state, action) => {
      state.loading = false;
      state.review = action.payload || null;
    });
    builder.addCase(generateCertificate.rejected, (state, action) => {
      state.loading = false;
      state.error =
        action.payload || "Error obteniendo información de certificado";
      state.review = null;
    });
    // 22 getByCertificateCode
    builder.addCase(getByCertificateCode.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.review = null;
    });
    builder.addCase(getByCertificateCode.fulfilled, (state, action) => {
      state.loading = false;
      state.review = action.payload || null;
    });
    builder.addCase(getByCertificateCode.rejected, (state, action) => {
      state.loading = false;
      state.error =
        action.payload || "Error obteniendo información de certificado";
      state.review = null;
    });
  },
});

export default reviewSlice.reducer;
